import { type ShareDocument, shareFileName, singleEventDocument } from '@escala/domain/share';

import { type IconSvg, loadFonts, loadIcons, loadPhotos } from './assets';
import { COLORS, canvasBlob, createCanvas } from './canvas';
import {
  CARD_GAP,
  FOOTER,
  MARGIN,
  type RenderContext,
  WIDTH,
  buildCards,
  cardHeight,
  drawCard,
  drawFooter,
  drawHeader,
  measureHeader,
} from './layout';

/**
 * A escala como imagem PNG: o cabeçalho, os cards dos eventos empilhados e o rodapé,
 * em 1080 px de largura (a do WhatsApp e do Instagram), desenhada em 2× para ficar
 * nítida no celular.
 */

export interface ShareOptions {
  org: string | null;
  color: string;
  /** `yyyy-mm-dd` */
  generatedAt: string;
  /** O SVG do ícone de cada ministério; sem ele, as pílulas ficam com a bolinha na cor. */
  iconSvg?: IconSvg;
  /**
   * Leva as fotos de perfil nos avatares. Desligado (o padrão), todos saem com as iniciais:
   * a imagem circula fora do app, e a foto é dado pessoal.
   */
  photos?: boolean;
}

export interface ShareFile {
  name: string;
  blob: Blob;
}

/**
 * Pixels por unidade: 2×, menos quando a imagem é tão alta que passaria do limite de
 * canvas dos navegadores (o Safari recusa acima de ~16,7 milhões de pixels).
 */
function scaleFor(height: number): number {
  return Math.min(2, Math.sqrt(16_000_000 / (WIDTH * height)), 16_000 / height);
}

async function renderContext(doc: ShareDocument, options: ShareOptions): Promise<RenderContext> {
  await loadFonts();

  return {
    org: options.org,
    color: options.color,
    generatedAt: options.generatedAt,
    photos: options.photos ? await loadPhotos(doc) : new Map(),
    icons: await loadIcons(doc, options.iconSvg),
  };
}

function draw(doc: ShareDocument, rc: RenderContext): HTMLCanvasElement {
  const { ctx: measure } = createCanvas(1, 1, 1);
  const cards = buildCards(measure, doc, rc);
  const header = measureHeader(measure, doc, rc);
  const body = cards.reduce((sum, card, i) => sum + cardHeight(card.pieces) + (i ? CARD_GAP : 0), 0);
  const height = header + 40 + body + 16 + FOOTER;
  const { canvas, ctx } = createCanvas(WIDTH, height, scaleFor(height));

  ctx.fillStyle = COLORS.page;
  ctx.fillRect(0, 0, WIDTH, height);
  let y = drawHeader(ctx, doc, rc) + 40;

  for (const card of cards) {
    drawCard(ctx, card.pieces, MARGIN, y, WIDTH - MARGIN * 2);
    y += cardHeight(card.pieces) + CARD_GAP;
  }

  drawFooter(ctx, height - FOOTER, rc);

  return canvas;
}

/** Uma imagem com todos os eventos do documento. */
export async function renderShareImage(doc: ShareDocument, options: ShareOptions): Promise<ShareFile> {
  const rc = await renderContext(doc, options);

  return { name: `${shareFileName(doc)}.png`, blob: await canvasBlob(draw(doc, rc), 'image/png') };
}

/** Uma imagem por evento — mais legível no WhatsApp quando o período tem muitos eventos. */
export async function renderShareImages(doc: ShareDocument, options: ShareOptions): Promise<ShareFile[]> {
  const rc = await renderContext(doc, options);

  return Promise.all(
    doc.events.map(async (event) => {
      const one = singleEventDocument(doc, event);

      return { name: `${shareFileName(one)}.png`, blob: await canvasBlob(draw(one, rc), 'image/png') };
    })
  );
}
